import React, { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import FieldConfigurationAPI from "../../../lib/api/FieldConfiguration";
import "./style.css";

// Searchable picker for a liability GL account, used by the Expense Cards
// section — mirrors SearchableEmployeeSelect's control/dropdown UX.
export default function SearchableGlAccountSelect({
  label,
  required,
  value,
  companyUuid,
  onChange,
  className = "",
  placeholder = "Search for GL account...",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!isOpen) return;

      setLoading(true);
      try {
        const result = await FieldConfigurationAPI.getLiabilityGlAccounts(
          searchTerm,
          companyUuid
        );
        if (result.status === 200 && Array.isArray(result.data)) {
          setAccounts(result.data);
        }
      } catch (error) {
        console.error("Error fetching GL accounts:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchAccounts, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, isOpen, companyUuid]);

  useEffect(() => {
    if (value && (!selectedAccount || selectedAccount.code !== value)) {
      const loadSelectedAccount = async () => {
        try {
          const result = await FieldConfigurationAPI.getLiabilityGlAccounts(
            value,
            companyUuid
          );
          if (result.status === 200 && Array.isArray(result.data)) {
            const match = result.data.find((a) => a.code === value);
            if (match) setSelectedAccount(match);
          }
        } catch (error) {
          console.error("Error loading selected GL account:", error);
        }
      };
      loadSelectedAccount();
    } else if (!value && selectedAccount) {
      setSelectedAccount(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, companyUuid]);

  const handleSelect = (account) => {
    setSelectedAccount(account);
    onChange(account.code);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedAccount(null);
    onChange("");
    setSearchTerm("");
  };

  const formatAccountLabel = (account) =>
    account.description
      ? `${account.code} - ${account.description}`
      : account.code;

  return (
    <div
      className={`searchable-gl-account-select ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="searchable-gl-account-select__label">
          {label}
          {required && <span className="danger">&nbsp;*</span>}
        </label>
      )}

      <div
        className={`searchable-gl-account-select__control ${
          isOpen ? "searchable-gl-account-select__control--open" : ""
        } ${disabled ? "searchable-gl-account-select__control--disabled" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="searchable-gl-account-select__value">
          {selectedAccount ? (
            <>
              <span>{formatAccountLabel(selectedAccount)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="searchable-gl-account-select__clear"
                  onClick={handleClear}
                  aria-label="Clear selection"
                >
                  <X size={16} />
                </button>
              )}
            </>
          ) : value ? (
            <span>{value}</span>
          ) : (
            <span className="searchable-gl-account-select__placeholder">
              {placeholder}
            </span>
          )}
        </div>
        <Search size={16} className="searchable-gl-account-select__icon" />
      </div>

      {isOpen && (
        <div className="searchable-gl-account-select__dropdown">
          <div className="searchable-gl-account-select__search">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search..."
              autoFocus
              className="searchable-gl-account-select__search-input"
            />
          </div>

          <div className="searchable-gl-account-select__options">
            {loading ? (
              <div className="searchable-gl-account-select__loading">
                Loading...
              </div>
            ) : accounts.length === 0 ? (
              <div className="searchable-gl-account-select__empty">
                No liability GL accounts found
              </div>
            ) : (
              accounts.map((account) => (
                <div
                  key={account.uuid || account.code}
                  className="searchable-gl-account-select__option"
                  onClick={() => handleSelect(account)}
                >
                  <div className="searchable-gl-account-select__option-code">
                    {account.code}
                  </div>
                  <div className="searchable-gl-account-select__option-name">
                    {account.description}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
